import { DIFFICULTIES } from "../lib/difficulty";
import { caseGymExtra } from "./cases-bgb-at-extra";
import type { CaseExercise } from "../types";

// Fachlich gegen BGB geprüft (reviewStatus "reviewed"). Finale beta-ready-Freigabe durch Vincent.
// Jeder Schritt trennt die richtige Antwort (correct) von vier Distraktor-Stufen:
//   einfach    - grob/offensichtlich falsch, reiner Abruf
//   medium     - ein plausibler Distraktor (typische Verwechslung), Rest klar
//   schwer     - mehrere plausible Distraktoren (konkurrierende Normen, feine Abgrenzung)
//   sehr-schwer- alle Optionen klingen vertretbar; nur saubere Subsumtion trennt
// Die App assembliert daraus adaptiv 4 Optionen je nach Niveau (siehe lib/difficulty.ts).
export const cases: CaseExercise[] = [
  {
    id: "case-minderjaehrig-smartphone",
    pathId: "hagen-jura",
    topic: "BGB AT - Minderjährigenrecht",
    title: "Smartphone-Kauf des Minderjährigen",
    level: "Anwendbar",
    levelNumber: 3,
    skillTags: ["bgb-at", "subsumtion"],
    reviewStatus: "reviewed",
    sachverhalt:
      "Der 16-jährige M kauft im Laden ein Smartphone für 600 Euro, ohne dass seine Eltern davon wissen. Er zahlt nicht sofort.",
    steps: [
      {
        question: "Welche Geschäftsfähigkeit hat M?",
        correct: ["Beschränkt geschäftsfähig"],
        distractors: {
          einfach: ["Voll geschäftsfähig", "Geschäftsunfähig", "Teilweise geschäftsunfähig"],
          medium: ["Voll geschäftsfähig, da über 14 Jahre", "Geschäftsunfähig nach § 104 BGB", "Bedingt geschäftsfähig je nach Geschäftswert"],
          schwer: [
            "Voll geschäftsfähig für Geschäfte des täglichen Lebens",
            "Geschäftsunfähig, weil noch nicht volljährig",
            "Beschränkt geschäftsfähig nur bei schriftlicher Vollmacht der Eltern",
          ],
          "sehr-schwer": [
            "Voll geschäftsfähig kraft Ermächtigung nach § 112 BGB",
            "Im Rahmen des § 113 BGB für dieses Geschäft unbeschränkt geschäftsfähig",
            "Geschäftsunfähig für Geschäfte über 105a BGB hinaus",
          ],
        },
        explanation:
          "§ 106 BGB: Wer das siebente, aber nicht das 18. Lebensjahr vollendet hat, ist beschränkt geschäftsfähig. M ist 16. §§ 112/113 (selbstständiger Erwerb/Dienstverhältnis) liegen nicht vor.",
        sources: [{ id: "bgb106", label: "§ 106 BGB", url: "https://www.gesetze-im-internet.de/bgb/__106.html" }],
      },
      {
        question: "Ist der Kaufvertrag für M lediglich rechtlich vorteilhaft (§ 107 BGB)?",
        correct: ["Nein - der Kaufvertrag begründet die Kaufpreiszahlungspflicht (§ 433 Abs. 2 BGB)"],
        distractors: {
          einfach: [
            "Ja - M bekommt ein Smartphone, das ist immer vorteilhaft",
            "Ja - Minderjährige dürfen alles unter 1000 Euro kaufen",
            "Nein - weil der Vertrag schriftlich geschlossen werden muss",
          ],
          medium: [
            "Ja - M erlangt das Eigentum am Smartphone, also einen Vorteil",
            "Ja - bei einem Bargeschäft des täglichen Lebens ist es vorteilhaft",
            "Nein - weil M den Kaufpreis noch nicht gezahlt hat",
          ],
          schwer: [
            "Ja - M erlangt das Eigentum am Smartphone, also einen rechtlichen Vorteil",
            "Ja - das dingliche Erfüllungsgeschäft (Übereignung) ist für M lediglich vorteilhaft",
            "Nein - weil der Vertrag mangels Einwilligung ohnehin schwebend unwirksam ist",
          ],
          "sehr-schwer": [
            "Ja - isoliert betrachtet verschafft das Verpflichtungsgeschäft M den Anspruch auf Übereignung (§ 433 Abs. 1)",
            "Ja - die Kaufpreiszahlungspflicht trifft den gesetzlichen Vertreter, nicht M selbst",
            "Nein - aber allein deshalb, weil § 107 nur auf das dingliche Geschäft abstellt",
          ],
        },
        explanation:
          "§ 107 BGB: Lediglich rechtlich vorteilhaft ist nur ein Geschäft ohne rechtliche Pflichten. Der Kaufvertrag begründet die Zahlungspflicht (§ 433 Abs. 2 BGB) und ist isoliert (Trennungsprinzip) zu prüfen; der wirtschaftliche Vorteil des Smartphones zählt nicht.",
        sources: [{ id: "bgb107", label: "§ 107 BGB", url: "https://www.gesetze-im-internet.de/bgb/__107.html" }],
      },
      {
        question: "Wie ist die Wirksamkeit des Vertrags ohne Einwilligung der Eltern zu beurteilen?",
        correct: ["Schwebend unwirksam bis zur Entscheidung der Eltern"],
        distractors: {
          einfach: ["Von Anfang an nichtig", "Voll wirksam", "Anfechtbar nach § 119 BGB"],
          medium: [
            "Von Anfang an nichtig nach § 105 BGB",
            "Voll wirksam, da M das Smartphone bereits erhalten hat",
            "Wirksam, aber durch die Eltern frei kündbar",
          ],
          schwer: [
            "Nichtig, weil M ohne Vertretungsmacht gehandelt hat",
            "Voll wirksam mit nachträglichem Anfechtungsrecht der Eltern",
            "Schwebend wirksam bis zum Widerruf durch M",
          ],
          "sehr-schwer": [
            "Schwebend unwirksam; die Eltern müssen den Vertrag jedoch binnen zwei Wochen genehmigen (§ 108 Abs. 2)",
            "Schwebend unwirksam, aber bereits jetzt durch M selbst genehmigungsfähig (§ 108 Abs. 3)",
            "Bis zur Volljährigkeit des M schwebend unwirksam und dann automatisch wirksam",
          ],
        },
        explanation:
          "§ 108 Abs. 1 BGB: Ohne erforderliche Einwilligung ist der Vertrag schwebend unwirksam - er wird durch Genehmigung wirksam oder durch Verweigerung endgültig unwirksam. Die Zwei-Wochen-Frist des Abs. 2 läuft nur auf Aufforderung des Vertragspartners; Abs. 3 betrifft den volljährig gewordenen M.",
        sources: [{ id: "bgb108", label: "§ 108 BGB", url: "https://www.gesetze-im-internet.de/bgb/__108.html" }],
      },
      {
        question: "Greift der Taschengeldparagraph (§ 110 BGB), wenn M noch nicht gezahlt hat?",
        correct: ["Nein - § 110 verlangt, dass die vertragsmäßige Leistung bereits bewirkt wurde"],
        distractors: {
          einfach: ["Ja - M wollte mit Taschengeld zahlen", "Ja - automatisch bei Minderjährigen", "Nein - § 110 gilt nur für Geschenke"],
          medium: [
            "Ja - M hätte mit seinem Taschengeld zahlen können",
            "Ja - sobald M eine Anzahlung leistet",
            "Nein - § 110 gilt nur für Geschäfte unter 100 Euro",
          ],
          schwer: [
            "Nein - aber nur, weil 600 Euro kein übliches Taschengeld sind",
            "Ja - es genügt, dass M die Mittel zur freien Verfügung hatte",
            "Ja - mit der Übergabe des Smartphones ist die Leistung bewirkt",
          ],
          "sehr-schwer": [
            "Ja - mit dem Vertragsschluss hat M bereits wirksam über sein Taschengeld im Sinne des § 110 verfügt",
            "Ja - § 110 lässt schon die Überlassung der Mittel zur freien Verfügung als Bewirken genügen",
            "Nein - allerdings nur, weil 600 Euro den betragsmäßigen Rahmen des § 110 übersteigen",
          ],
        },
        explanation:
          "§ 110 BGB setzt voraus, dass der Minderjährige die vertragsmäßige Leistung (Kaufpreiszahlung) mit überlassenen Mitteln bewirkt. Erst das Bewirken macht den Vertrag wirksam - hier hat M noch nicht gezahlt. Einen betragsmäßigen Höchstwert kennt § 110 nicht.",
        sources: [{ id: "bgb110", label: "§ 110 BGB", url: "https://www.gesetze-im-internet.de/bgb/__110.html" }],
      },
    ],
  },
  {
    id: "case-stellvertretung-fahrrad",
    pathId: "hagen-jura",
    topic: "BGB AT - Stellvertretung",
    title: "Vertreter ohne Vertretungsmacht",
    level: "Anwendbar",
    levelNumber: 3,
    skillTags: ["bgb-at", "subsumtion", "stellvertretung"],
    reviewStatus: "reviewed",
    sachverhalt:
      "V verkauft im Namen des G dessen Fahrrad an K, obwohl G ihn dazu nie bevollmächtigt hat. K weiß nichts von der fehlenden Vollmacht. Als G davon erfährt, will er mit dem Geschäft nichts zu tun haben.",
    steps: [
      {
        question: "Unter welchen Voraussetzungen wirkt die Willenserklärung des V unmittelbar für und gegen G (§ 164 Abs. 1 BGB)?",
        correct: ["Eigene Willenserklärung, abgegeben im Namen des G, innerhalb bestehender Vertretungsmacht"],
        distractors: {
          einfach: [
            "Es genügt, dass V erkennbar im Namen des G auftritt",
            "V muss lediglich volljährig und geschäftsfähig sein",
            "Es genügt, dass V den Willen des G als Bote übermittelt",
          ],
          medium: [
            "Handeln im fremden Namen mit Offenkundigkeit genügt",
            "V muss im eigenen Namen, aber für Rechnung des G handeln",
            "Eine Anscheinsvollmacht ist stets ausreichend",
          ],
          schwer: [
            "Eigene Willenserklärung im eigenen Namen, solange das Geschäft wirtschaftlich G zugutekommt",
            "Handeln im Namen des G genügt; die Vertretungsmacht wird vermutet",
            "Übermittlung einer fertigen Willenserklärung des G (Botenschaft)",
          ],
          "sehr-schwer": [
            "Eigene Willenserklärung im Namen des G; die Vertretungsmacht ist über § 177 nachholbar und daher keine Wirksamkeitsvoraussetzung",
            "Offenkundiges Handeln für G; die Vertretungsmacht betrifft nur das Innenverhältnis",
            "Eigene Willenserklärung im Namen des G; auf die Vertretungsmacht kommt es bei Verkehrsgeschäften nicht an",
          ],
        },
        explanation:
          "§ 164 Abs. 1 BGB: Eine eigene Willenserklärung wirkt für und gegen den Vertretenen nur, wenn sie im fremden Namen und innerhalb der Vertretungsmacht abgegeben wird. Alle drei Voraussetzungen müssen zusammen vorliegen; fehlt die Vertretungsmacht, greift § 177.",
        sources: [{ id: "bgb164", label: "§ 164 BGB", url: "https://www.gesetze-im-internet.de/bgb/__164.html" }],
      },
      {
        question: "V hatte keine Vertretungsmacht. Wie ist der Kaufvertrag zunächst zu beurteilen (§ 177 Abs. 1 BGB)?",
        correct: ["Schwebend unwirksam - er hängt von der Genehmigung des G ab"],
        distractors: {
          einfach: ["Von Anfang an nichtig", "Voll wirksam; V haftet nur daneben", "Wirksam, aber durch G frei widerruflich"],
          medium: ["Nichtig nach § 134 BGB", "Voll wirksam, da K gutgläubig war", "Schwebend wirksam bis zum Widerruf des K"],
          schwer: [
            "Nichtig, weil ein Vertreter ohne Vertretungsmacht keine wirksame Willenserklärung abgeben kann",
            "Voll wirksam kraft Rechtsscheins (Anscheinsvollmacht)",
            "Schwebend unwirksam nur gegenüber V, nicht gegenüber G",
          ],
          "sehr-schwer": [
            "Schwebend unwirksam; die Genehmigung des G wirkt jedoch erst ab ihrer Erklärung (ex nunc)",
            "Schwebend unwirksam; bleibt die Genehmigung aus, wird der Vertrag mit Fristablauf automatisch wirksam",
            "Zunächst voll wirksam; G kann ihn binnen zwei Wochen zurückweisen",
          ],
        },
        explanation:
          "§ 177 Abs. 1 BGB: Der Vertrag ist schwebend unwirksam und hängt von der Genehmigung des Vertretenen ab. Die Genehmigung wirkt nach § 184 Abs. 1 BGB zurück (ex tunc); Schweigen auf eine Aufforderung gilt als Verweigerung (§ 177 Abs. 2).",
        sources: [{ id: "bgb177", label: "§ 177 BGB", url: "https://www.gesetze-im-internet.de/bgb/__177.html" }],
      },
      {
        question: "G verweigert die Genehmigung. Wie haftet V dem K gegenüber (§ 179 Abs. 1 BGB)?",
        correct: ["Nach Wahl des K auf Erfüllung oder Schadensersatz"],
        distractors: {
          einfach: [
            "Gar nicht - er ist nur als Vertreter aufgetreten",
            "Nur G haftet, weil es sein Fahrrad war",
            "K hat allein einen Anspruch auf Ersatz seiner Aufwendungen",
          ],
          medium: [
            "Nur auf das negative Interesse (Vertrauensschaden)",
            "Nur auf Schadensersatz, nicht auf Erfüllung",
            "Auf Erfüllung, jedoch nur gemeinsam mit G",
          ],
          schwer: [
            "Nach Wahl des K, jedoch von vornherein begrenzt auf das negative Interesse",
            "Auf Schadensersatz nach § 280 Abs. 1 wegen Pflichtverletzung",
            "Gesamtschuldnerisch mit G auf Erfüllung",
          ],
          "sehr-schwer": [
            "Nach Wahl des K auf Erfüllung oder Schadensersatz - jedoch nur, wenn V die fehlende Vertretungsmacht nicht kannte",
            "Auf das positive Interesse, der Höhe nach aber begrenzt auf das negative Interesse des K",
            "Nur auf das negative Interesse; das Wahlrecht des K besteht erst nach gerichtlicher Feststellung",
          ],
        },
        explanation:
          "§ 179 Abs. 1 BGB: Verweigert der Vertretene die Genehmigung, haftet der Vertreter ohne Vertretungsmacht nach Wahl des anderen Teils auf Erfüllung oder Schadensersatz. Die Beschränkung auf das negative Interesse (Abs. 2) gilt nur, wenn der Vertreter den Mangel nicht kannte - V kannte ihn hier.",
        sources: [{ id: "bgb179", label: "§ 179 BGB", url: "https://www.gesetze-im-internet.de/bgb/__179.html" }],
      },
      {
        question: "Würde es etwas ändern, wenn K die fehlende Vertretungsmacht gekannt hätte (§ 179 Abs. 3 BGB)?",
        correct: ["Ja - die Haftung des V entfällt, wenn K den Mangel kannte oder kennen musste"],
        distractors: {
          einfach: [
            "Nein - die Haftung bleibt bei Kenntnis unverändert bestehen",
            "K könnte trotz Kenntnis Erfüllung von G verlangen",
            "Die Kenntnis des K verdoppelt seinen Anspruch gegen V",
          ],
          medium: [
            "Nein - nur positive Kenntnis schadet, fahrlässige Unkenntnis nicht",
            "Ja - aber nur, wenn auch G die Genehmigung verweigert hat",
            "Nein - K kann sich auf Vertrauensschutz nach § 173 berufen",
          ],
          schwer: [
            "Ja - aber nur, wenn zusätzlich V selbst gutgläubig war",
            "Nein - die Kenntnis des K mindert die Haftung nur auf das negative Interesse",
            "Ja - die Haftung entfällt, sobald K den Mangel hätte erkennen können, unabhängig von eigener Fahrlässigkeit",
          ],
          "sehr-schwer": [
            "Nein - § 179 Abs. 3 schließt nur die Haftung auf das positive Interesse aus; das negative Interesse schuldet V weiter",
            "Ja - jedoch nur bei positiver Kenntnis; bloßes Kennenmüssen lässt die Haftung bestehen",
            "Nein - die Kenntnis des K verlagert die Haftung lediglich von V auf den Vertretenen G",
          ],
        },
        explanation:
          "§ 179 Abs. 3 Satz 1 BGB: Der Vertreter haftet nicht, wenn der andere Teil den Mangel der Vertretungsmacht kannte oder kennen musste (fahrlässige Unkenntnis genügt). Wer das Risiko kennt oder kennen muss, ist nicht schutzwürdig.",
        sources: [{ id: "bgb179", label: "§ 179 BGB", url: "https://www.gesetze-im-internet.de/bgb/__179.html" }],
      },
    ],
  },
  {
    id: "case-anfechtung-aktenordner",
    pathId: "hagen-jura",
    topic: "BGB AT - Anfechtung",
    title: "Vertippt bei der Online-Bestellung",
    level: "Anwendbar",
    levelNumber: 3,
    skillTags: ["bgb-at", "subsumtion", "anfechtung"],
    reviewStatus: "reviewed",
    sachverhalt:
      "K will im Online-Shop 10 Aktenordner bestellen, vertippt sich aber und gibt 100 ein. Er klickt auf Kaufen. Erst als die Bestätigung über 100 Ordner eintrifft, bemerkt er den Fehler und möchte sich vom Vertrag lösen.",
    steps: [
      {
        question: "Welcher Irrtum liegt vor, wenn K etwas anderes erklärt, als er erklären wollte (§ 119 Abs. 1 BGB)?",
        correct: ["Erklärungsirrtum (§ 119 Abs. 1 Var. 2 BGB) - Irrtum in der Erklärungshandlung"],
        distractors: {
          einfach: [
            "Inhaltsirrtum (§ 119 Abs. 1 Var. 1 BGB) - Irrtum über die Bedeutung",
            "Eigenschaftsirrtum (§ 119 Abs. 2 BGB)",
            "Unbeachtlicher Motivirrtum",
          ],
          medium: [
            "Inhaltsirrtum, weil K über den Inhalt seiner Bestellung irrte",
            "Kalkulationsirrtum, der hier zur Anfechtung berechtigt",
            "Übermittlungsfehler nach § 120 BGB",
          ],
          schwer: [
            "Inhaltsirrtum (§ 119 Abs. 1 Var. 1), weil K die Stückzahl falsch verstanden hat",
            "Übermittlungsirrtum (§ 120 BGB), da das System die Zahl übertragen hat",
            "Unbeachtlicher Irrtum im Beweggrund (Motivirrtum)",
          ],
          "sehr-schwer": [
            "Inhaltsirrtum (§ 119 Abs. 1 Var. 1), weil sich K über die rechtliche Tragweite seiner Erklärung irrte",
            "Erklärungsirrtum, der hier aber nach § 116 Satz 1 unbeachtlich ist",
            "Doppelirrtum, der nur über § 119 Abs. 2 (Eigenschaftsirrtum) anfechtbar ist",
          ],
        },
        explanation:
          "§ 119 Abs. 1 Var. 2 BGB: Beim Vertippen will der Erklärende dieses Zeichen gar nicht setzen - er irrt in der Erklärungshandlung (Erklärungsirrtum). Der Inhaltsirrtum (Var. 1) betrifft die Bedeutung des Erklärten; § 116 (geheimer Vorbehalt) und § 120 (Übermittlung durch eine Person) passen nicht.",
        sources: [{ id: "bgb119", label: "§ 119 BGB", url: "https://www.gesetze-im-internet.de/bgb/__119.html" }],
      },
      {
        question: "Was muss K tun, um sich vom Vertrag zu lösen (§ 143 BGB)?",
        correct: ["Die Anfechtung gegenüber dem Verkäufer als Anfechtungsgegner erklären"],
        distractors: {
          einfach: [
            "Nichts - der Vertrag ist bei einem Irrtum automatisch nichtig",
            "Den Vertrag außerordentlich kündigen",
            "Nach § 323 BGB vom Vertrag zurücktreten",
          ],
          medium: [
            "Die Anfechtung gegenüber dem zuständigen Gericht erklären",
            "Den Widerruf nach § 355 BGB ausüben",
            "Die Anfechtung öffentlich gegenüber jedermann erklären",
          ],
          schwer: [
            "Die Anfechtung erklären und zusätzlich gerichtlich geltend machen",
            "Die Nichtigkeit nach § 142 ohne gesonderte Erklärung herbeiführen",
            "Die Anfechtung nur gegenüber dem Vertreter des Verkäufers erklären",
          ],
          "sehr-schwer": [
            "Die Anfechtung gegenüber dem Anfechtungsgegner; das ist bei einem Vertrag zugunsten Dritter der begünstigte Dritte",
            "Die Anfechtung gegenüber dem Verkäufer; sie bedarf jedoch der notariellen Beurkundung",
            "Die Anfechtung gegenüber dem Verkäufer; mangels Frist kann er sie bis zur Verjährung erklären",
          ],
        },
        explanation:
          "§ 143 Abs. 1, 2 BGB: Die Anfechtung erfolgt durch (formfreie) Erklärung gegenüber dem Anfechtungsgegner; das ist beim Vertrag der andere Vertragsteil. Ohne Anfechtungserklärung bleibt der Vertrag wirksam.",
        sources: [{ id: "bgb143", label: "§ 143 BGB", url: "https://www.gesetze-im-internet.de/bgb/__143.html" }],
      },
      {
        question: "Bis wann muss K die Anfechtung erklären (§ 121 Abs. 1 BGB)?",
        correct: ["Unverzüglich (ohne schuldhaftes Zögern) nach Kenntnis vom Anfechtungsgrund"],
        distractors: {
          einfach: [
            "Innerhalb von zwei Wochen ab Vertragsschluss",
            "Innerhalb der regelmäßigen Verjährungsfrist von drei Jahren",
            "Jederzeit, solange der Vertrag noch nicht erfüllt ist",
          ],
          medium: [
            "Innerhalb von 14 Tagen wie beim Widerruf",
            "Sofort, das heißt noch am selben Tag",
            "Innerhalb angemessener Frist ab Vertragsschluss",
          ],
          schwer: [
            "Unverzüglich, aber gerechnet ab Vertragsschluss statt ab Kenntnis",
            "Innerhalb von zwei Wochen ab Kenntnis als gesetzlicher Regelfrist",
            "Unverzüglich, spätestens aber binnen sechs Monaten ab Kenntnis",
          ],
          "sehr-schwer": [
            "Unverzüglich nach Kenntnis, absolut spätestens drei Jahre nach Abgabe der Erklärung (§ 121 Abs. 2)",
            "Unverzüglich; die Frist beginnt jedoch schon mit objektiver Erkennbarkeit, nicht erst mit Kenntnis",
            "Unverzüglich nach Kenntnis; ein Zögern von wenigen Tagen ist dabei stets unschädlich",
          ],
        },
        explanation:
          "§ 121 Abs. 1 BGB: Die Anfechtung muss ohne schuldhaftes Zögern (unverzüglich) erfolgen, nachdem der Anfechtungsberechtigte vom Anfechtungsgrund Kenntnis erlangt hat. Maßgeblich ist die positive Kenntnis; die absolute Grenze des Abs. 2 liegt bei zehn Jahren ab Abgabe.",
        sources: [{ id: "bgb121", label: "§ 121 BGB", url: "https://www.gesetze-im-internet.de/bgb/__121.html" }],
      },
      {
        question: "Welche Rechtsfolge hat die wirksame Anfechtung (§ 142 Abs. 1 BGB)?",
        correct: ["Der Vertrag ist als von Anfang an (ex tunc) nichtig anzusehen"],
        distractors: {
          einfach: [
            "Der Vertrag endet erst ab der Anfechtung (ex nunc)",
            "Der Vertrag bleibt wirksam; K schuldet nur Schadensersatz",
            "Der Vertrag wird bis zu einer Genehmigung schwebend unwirksam",
          ],
          medium: [
            "Der Vertrag ist ab Zugang der Anfechtung für die Zukunft nichtig",
            "Der Vertrag wird rückabgewickelt, bleibt aber wirksam",
            "Der Vertrag ist schwebend unwirksam bis zur Bestätigung",
          ],
          schwer: [
            "Der Vertrag ist nichtig, jedoch erst ab Rechtskraft eines Gestaltungsurteils",
            "Der Vertrag ist ex tunc nichtig; das Verfügungsgeschäft bleibt nach dem Abstraktionsprinzip stets wirksam",
            "Der Vertrag ist von Anfang an nichtig; K schuldet jedoch verschuldensunabhängig das positive Interesse",
          ],
          "sehr-schwer": [
            "Der Vertrag ist ex tunc nichtig; K schuldet dem Verkäufer nach § 122 den entgangenen Gewinn (positives Interesse)",
            "Der Vertrag ist ex tunc nichtig, weshalb das dingliche Erfüllungsgeschäft nach dem Grundsatz der Fehleridentität zwingend mit unwirksam ist",
            "Der Vertrag ist ex tunc nichtig; bereits Geleistetes wird nach den §§ 346 ff. (Rücktrittsrecht) zurückgewährt",
          ],
        },
        explanation:
          "§ 142 Abs. 1 BGB: Das angefochtene Rechtsgeschäft ist als von Anfang an nichtig anzusehen (ex tunc). K schuldet nach § 122 nur den Vertrauensschaden (negatives Interesse); rückabgewickelt wird über das Bereicherungsrecht (§ 812), nicht über Rücktrittsrecht.",
        sources: [{ id: "bgb142", label: "§ 142 BGB", url: "https://www.gesetze-im-internet.de/bgb/__142.html" }],
      },
    ],
  },
  ...caseGymExtra,
];

export interface CaseIssue {
  caseId: string;
  message: string;
}

export function validateCases(bank: CaseExercise[] = cases): CaseIssue[] {
  const issues: CaseIssue[] = [];
  for (const c of bank) {
    if (c.steps.length === 0) issues.push({ caseId: c.id, message: "Fall ohne Schritte." });
    c.steps.forEach((step, i) => {
      const where = `${c.id}#step-${i + 1}`;
      if (step.correct.length === 0) issues.push({ caseId: where, message: "Schritt ohne richtige Antwort." });
      for (const d of DIFFICULTIES) {
        if ((step.distractors[d]?.length ?? 0) !== 3)
          issues.push({ caseId: where, message: `Stufe ${d} braucht genau 3 Distraktoren.` });
      }
      if (!step.explanation) issues.push({ caseId: where, message: "Erklärung fehlt." });
      if (step.sources.length === 0) issues.push({ caseId: where, message: "Quelle fehlt." });
    });
  }
  return issues;
}
